/**
 * 认知偏差可视化动画
 * Cognitive Bias Animation - 心理学教育
 * 
 * 核心效果：
 * 1. 展示常见认知偏差（锚定效应、损失厌恶、确认偏误等）
 * 2. 每个偏差用可视化方式呈现其机制
 * 3. 动态对比理性思维与偏差思维
 * 
 * 视觉规范：
 * - 背景：深黑 #0A0E27
 * - 主色：暖橙 #FF6B35
 * - 辅色：冷青 #4ECDC4
 * - 大标题：80px
 */

import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

// ==================== 类型定义 ====================

type CognitiveBias = {
  id: number;
  name: string;
  nameEn: string;
  icon: string;
  description: string;
  example: string;
  impact: string;
  color: string;
};

type CognitiveBiasAnimationProps = {
  primaryColor?: string;
  secondaryColor?: string;
  backgroundColor?: string;
};

// ==================== 数据配置 ====================

const COGNITIVE_BIASES: CognitiveBias[] = [
  {
    id: 1,
    name: '锚定效应',
    nameEn: 'Anchoring Bias',
    icon: '⚓',
    description: '过度依赖首次信息',
    example: '先看到高价，就觉得打折后便宜',
    impact: '影响决策判断',
    color: '#FF6B35',
  },
  {
    id: 2,
    name: '损失厌恶',
    nameEn: 'Loss Aversion',
    icon: '💔',
    description: '损失比收益影响更大',
    example: '损失100元的痛苦 > 获得100元的快乐',
    impact: '导致风险回避',
    color: '#FF8C5A',
  },
  {
    id: 3,
    name: '确认偏误',
    nameEn: 'Confirmation Bias',
    icon: '🔍',
    description: '只寻找支持自己观点的证据',
    example: '相信某个观点，就只关注支持它的信息',
    impact: '强化既有偏见',
    color: '#FFB088',
  },
  {
    id: 4,
    name: '可得性启发',
    nameEn: 'Availability Heuristic',
    icon: '💭',
    description: '根据记忆容易程度判断概率',
    example: '刚看到飞机失事新闻，就觉得飞行危险',
    impact: '扭曲风险评估',
    color: '#4ECDC4',
  },
  {
    id: 5,
    name: '过度自信',
    nameEn: 'Overconfidence',
    icon: '😎',
    description: '高估自己的能力和知识',
    example: '90%的司机认为驾驶技术高于平均水平',
    impact: '导致冒险决策',
    color: '#45B7AF',
  },
];

// ==================== 组件定义 ====================

export const CognitiveBiasAnimation: React.FC<CognitiveBiasAnimationProps> = ({
  primaryColor = '#FF6B35',
  secondaryColor = '#4ECDC4',
  backgroundColor = '#0A0E27',
}) => {
  const frame = useCurrentFrame();
  const { fps, durationInFrames } = useVideoConfig();

  // === 时间分配：标题区 12% | 5个偏差卡片平分 73% | 结尾区 15% ===
  const TITLE_DURATION = Math.floor(durationInFrames * 0.12);
  const CLOSING_DURATION = Math.floor(durationInFrames * 0.15);
  const CONTENT_DURATION = durationInFrames - TITLE_DURATION - CLOSING_DURATION;
  const BIAS_DURATION = Math.floor(CONTENT_DURATION / COGNITIVE_BIASES.length);
  const CLOSING_START = TITLE_DURATION + COGNITIVE_BIASES.length * BIAS_DURATION;

  // 标题动画
  const titleOpacity = spring({
    frame,
    fps,
    config: { damping: 20, stiffness: 100 },
  });

  const titleScale = interpolate(
    spring({
      frame: frame - 5,
      fps,
      config: { damping: 12, stiffness: 80 },
    }),
    [0, 1],
    [0.9, 1]
  );

  // 当前展示的偏差索引（基于均匀时间分配）
  const currentBiasIndex = frame < TITLE_DURATION
    ? 0
    : Math.min(
        Math.floor((frame - TITLE_DURATION) / BIAS_DURATION),
        COGNITIVE_BIASES.length - 1
      );
  const currentBias = COGNITIVE_BIASES[currentBiasIndex];

  // 每张卡片的本地帧（用于入场动画）
  const biasStartFrame = TITLE_DURATION + currentBiasIndex * BIAS_DURATION;
  const biasLocalFrame = Math.max(0, frame - biasStartFrame);

  const cardEnterProgress = spring({
    frame: biasLocalFrame,
    fps,
    config: { damping: 18, stiffness: 80 },
  });

  const cardTranslateY = interpolate(cardEnterProgress, [0, 1], [40, 0]);

  return (
    <AbsoluteFill style={{ backgroundColor }}>
      {/* 主标题 */}
      <div
        style={{
          position: 'absolute',
          top: '6%',
          left: '50%',
          transform: `translateX(-50%) scale(${titleScale})`,
          textAlign: 'center',
          opacity: titleOpacity,
        }}
      >
        <h1
          style={{
            fontSize: 80,
            fontWeight: 'bold',
            color: primaryColor,
            margin: 0,
            fontFamily: 'PingFang SC, Microsoft YaHei, sans-serif',
          }}
        >
          认知偏差
        </h1>
        <p
          style={{
            fontSize: 30,
            color: '#9CA3AF',
            marginTop: 8,
            fontFamily: 'PingFang SC, Microsoft YaHei, sans-serif',
          }}
        >
          Cognitive Biases · 理解思维的陷阱
        </p>
      </div>

      {/* 主内容区域 - 当前偏差展示 */}
      {frame >= TITLE_DURATION && currentBias && (
        <div
          style={{
            position: 'absolute',
            top: '32%',
            left: '50%',
            transform: `translateX(-50%) translateY(${cardTranslateY}px)`,
            width: '85%',
            maxWidth: 1400,
          }}
        >
          {/* 大卡片 */}
          <div
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.03)',
              borderRadius: 24,
              padding: '48px 60px',
              border: `3px solid ${currentBias.color}`,
              boxShadow: `0 20px 60px ${currentBias.color}30`,
              opacity: cardEnterProgress,
            }}
          >
            {/* 偏差标题行 */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: 30,
              }}
            >
              <span style={{ fontSize: 64, marginRight: 24 }}>{currentBias.icon}</span>
              <div>
                <h2
                  style={{
                    fontSize: 56,
                    fontWeight: 'bold',
                    color: currentBias.color,
                    margin: 0,
                    fontFamily: 'PingFang SC, Microsoft YaHei, sans-serif',
                  }}
                >
                  {currentBias.name}
                </h2>
                <p
                  style={{
                    fontSize: 30,
                    color: secondaryColor,
                    margin: '8px 0 0 0',
                    fontFamily: 'Arial, sans-serif',
                  }}
                >
                  {currentBias.nameEn}
                </p>
              </div>
            </div>

            {/* 描述 */}
            <div
              style={{
                marginBottom: 32,
              }}
            >
              <p
                style={{
                  fontSize: 32,
                  color: '#E5E7EB',
                  margin: 0,
                  lineHeight: 1.6,
                  fontFamily: 'PingFang SC, Microsoft YaHei, sans-serif',
                }}
              >
                {currentBias.description}
              </p>
            </div>

            {/* 示例和影响 */}
            <div
              style={{
                display: 'flex',
                gap: 40,
                marginTop: 32,
              }}
            >
              {/* 示例 */}
              <div
                style={{
                  flex: 1,
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: 16,
                  padding: '24px 28px',
                }}
              >
                <h3
                  style={{
                    fontSize: 28,
                    fontWeight: 'bold',
                    color: secondaryColor,
                    margin: '0 0 12px 0',
                    fontFamily: 'PingFang SC, Microsoft YaHei, sans-serif',
                  }}
                >
                  💡 典型示例
                </h3>
                <p
                  style={{
                    fontSize: 26,
                    color: '#D1D5DB',
                    margin: 0,
                    lineHeight: 1.6,
                    fontFamily: 'PingFang SC, Microsoft YaHei, sans-serif',
                  }}
                >
                  {currentBias.example}
                </p>
              </div>

              {/* 影响 */}
              <div
                style={{
                  flex: 1,
                  backgroundColor: `${currentBias.color}15`,
                  borderRadius: 16,
                  padding: '24px 28px',
                  border: `1px solid ${currentBias.color}40`,
                }}
              >
                <h3
                  style={{
                    fontSize: 28,
                    fontWeight: 'bold',
                    color: currentBias.color,
                    margin: '0 0 12px 0',
                    fontFamily: 'PingFang SC, Microsoft YaHei, sans-serif',
                  }}
                >
                  ⚠️ 实际影响
                </h3>
                <p
                  style={{
                    fontSize: 26,
                    color: '#D1D5DB',
                    margin: 0,
                    lineHeight: 1.6,
                    fontFamily: 'PingFang SC, Microsoft YaHei, sans-serif',
                  }}
                >
                  {currentBias.impact}
                </p>
              </div>
            </div>
          </div>

          {/* 进度指示器 */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: 12,
              marginTop: 40,
            }}
          >
            {COGNITIVE_BIASES.map((bias, index) => (
              <div
                key={bias.id}
                style={{
                  width: index === currentBiasIndex ? 40 : 12,
                  height: 12,
                  borderRadius: 6,
                  backgroundColor:
                    index === currentBiasIndex
                      ? bias.color
                      : 'rgba(255, 255, 255, 0.2)',
                  transition: 'all 0.3s ease',
                }}
              />
            ))}
          </div>
        </div>
      )}

      {/* 底部提示 */}
      <div
        style={{
          position: 'absolute',
          bottom: '5%',
          left: '50%',
          transform: 'translateX(-50%)',
          textAlign: 'center',
          opacity: spring({
            frame: frame - CLOSING_START,
            fps,
            config: { damping: 20, stiffness: 100 },
          }),
        }}
      >
        <p
          style={{
            fontSize: 28,
            color: '#9CA3AF',
            margin: 0,
            fontFamily: 'PingFang SC, Microsoft YaHei, sans-serif',
          }}
        >
          认识偏差 · 克服偏见 · 做出理性决策
        </p>
      </div>

      {/* 装饰性元素 */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          pointerEvents: 'none',
          opacity: 0.08,
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: '20%',
            left: '10%',
            width: 200,
            height: 200,
            background: `radial-gradient(circle, ${primaryColor} 0%, transparent 70%)`,
            filter: 'blur(50px)',
          }}
        />
        <div
          style={{
            position: 'absolute',
            bottom: '25%',
            right: '12%',
            width: 180,
            height: 180,
            background: `radial-gradient(circle, ${secondaryColor} 0%, transparent 70%)`,
            filter: 'blur(45px)',
          }}
        />
      </div>
    </AbsoluteFill>
  );
};

// ==================== 导出默认配置 ====================

export const defaultCognitiveBiasProps: CognitiveBiasAnimationProps = {
  primaryColor: '#FF6B35',
  secondaryColor: '#4ECDC4',
  backgroundColor: '#0A0E27',
};
